<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Complete Household Visit</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Visit Summary -->
          <div class="mb-4">
            <h6 class="mb-3">Visit Summary</h6>
            <div class="card bg-light">
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <p class="mb-2">
                      <strong>Household:</strong> {{ visit.household?.household_number || visit.household_number || 'N/A' }}<br>
                      <strong>Head:</strong> {{ visit.household?.household_head_name || visit.household_head_name || 'Not assigned' }}<br>
                      <strong>Visit Type:</strong> {{ getVisitTypeLabel(visit.visit?.visit_type || visit.visit_type) }}
                    </p>
                  </div>
                  <div class="col-md-6">
                    <p class="mb-2">
                      <strong>Start Time:</strong> {{ formatDateTime(visit.visit?.visit_date || visit.visit_date) }}<br>
                      <strong>Duration:</strong> {{ calculateDuration() }} minutes<br>
                      <strong>Services Provided:</strong> {{ services.length }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Services Provided Summary -->
          <div v-if="services.length > 0" class="mb-4">
            <h6 class="mb-3">Services Provided During Visit</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Service Type</th>
                  <th>Member</th>
                  <th>Details</th>
                  <th>Category</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="service in services" :key="service.service_id">
                  <td>
                      <span :class="'badge ' + getServiceBadgeClass(service.service_category)">
                        {{ formatServiceType(service.service_type) }}
                      </span>
                  </td>
                  <td>{{ service.member_name || 'Household' }}</td>
                  <td>{{ formatServiceDetails(service.service_details) }}</td>
                  <td>
                    <span class="text-capitalize">{{ service.service_category }}</span>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Key Findings -->
          <div class="mb-4">
            <h6 class="mb-3">Key Findings & Observations</h6>

            <!-- Health Issues Identified -->
            <div class="mb-3">
              <label class="form-label">Health Issues Identified</label>
              <div class="row g-2">
                <div class="col-md-4" v-for="issue in healthIssues" :key="issue.value">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox"
                           :id="'issue_' + issue.value"
                           :value="issue.value"
                           v-model="findings.healthIssues">
                    <label class="form-check-label" :for="'issue_' + issue.value">
                      {{ issue.label }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Environmental Concerns -->
            <div class="mb-3">
              <label class="form-label">Environmental Concerns</label>
              <div class="row g-2">
                <div class="col-md-4" v-for="concern in environmentalConcerns" :key="concern.value">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox"
                           :id="'concern_' + concern.value"
                           :value="concern.value"
                           v-model="findings.environmentalConcerns">
                    <label class="form-check-label" :for="'concern_' + concern.value">
                      {{ concern.label }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Visit Notes -->
          <div class="mb-4">
            <label class="form-label">Visit Notes</label>
            <textarea v-model="completionForm.notes" class="form-control" rows="4"
                      :placeholder="'Document key observations, actions taken, and any important information...\n\nExisting notes: ' + (notes || 'None')"></textarea>
          </div>

          <!-- Follow-up Required -->
          <div class="mb-4">
            <div class="form-check form-switch mb-3">
              <input v-model="completionForm.followUpRequired" class="form-check-input"
                     type="checkbox" id="followUpSwitch">
              <label class="form-check-label" for="followUpSwitch">
                Follow-up visit required
              </label>
            </div>

            <!-- Follow-up Details -->
            <div v-if="completionForm.followUpRequired" class="ps-4">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Follow-up Date <span class="text-danger">*</span></label>
                  <input v-model="completionForm.followUpDate" type="date" class="form-control"
                         :min="tomorrow" required>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Priority</label>
                  <select v-model="completionForm.followUpPriority" class="form-select">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label">Follow-up Reason</label>
                <textarea v-model="completionForm.followUpReason" class="form-control" rows="2"
                          placeholder="Reason for follow-up visit..."></textarea>
              </div>
            </div>
          </div>

          <!-- Referrals Made -->
          <div class="mb-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0">Referrals Made</h6>
              <button @click="showAddReferral = true" class="btn btn-sm btn-outline-primary">
                <i class="bx bx-plus"></i> Add Referral
              </button>
            </div>

            <div v-if="referrals.length === 0" class="text-center text-muted py-3 border rounded">
              No referrals made during this visit
            </div>

            <div v-else class="list-group">
              <div v-for="(referral, index) in referrals" :key="index"
                   class="list-group-item">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 class="mb-1">{{ referral.memberName }}</h6>
                    <p class="mb-0 text-muted">
                      <strong>To:</strong> {{ referral.referralTo }}<br>
                      <strong>Reason:</strong> {{ referral.reason }}
                    </p>
                  </div>
                  <button @click="removeReferral(index)" class="btn btn-sm btn-danger">
                    <i class="bx bx-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Referral Form -->
          <div v-if="showAddReferral" class="card border-primary mb-4">
            <div class="card-body">
              <h6 class="card-title">Quick Referral</h6>
              <div class="row">
                <div class="col-md-6 mb-2">
                  <select v-model="quickReferral.memberId" class="form-select form-select-sm">
                    <option value="">Select Member</option>
                    <option v-for="member in members" :key="member.member_id"
                            :value="member.member_id">
                      {{ member.first_name }} {{ member.surname }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6 mb-2">
                  <input v-model="quickReferral.referralTo" type="text"
                         class="form-control form-control-sm" placeholder="Refer to (e.g., Hospital name)...">
                </div>
                <div class="col-12 mb-2">
                  <input v-model="quickReferral.reason" type="text"
                         class="form-control form-control-sm" placeholder="Reason for referral...">
                </div>
                <div class="col-12">
                  <button @click="addQuickReferral" class="btn btn-sm btn-primary me-2"
                          :disabled="!quickReferral.memberId || !quickReferral.referralTo">
                    Add
                  </button>
                  <button @click="cancelQuickReferral" class="btn btn-sm btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Model Household Evaluation -->
          <div v-if="canEvaluateModelHousehold" class="mb-4">
            <div class="alert alert-info">
              <h6 class="alert-heading">
                <i class="bx bx-star"></i> Model Household Evaluation
              </h6>
              <p class="mb-2">This household meets criteria for model household status:</p>
              <ul class="mb-2">
                <li v-for="criteria in metCriteria" :key="criteria">{{ criteria }}</li>
              </ul>
              <div class="form-check">
                <input v-model="completionForm.recommendModelStatus" class="form-check-input"
                       type="checkbox" id="modelStatusCheck">
                <label class="form-check-label" for="modelStatusCheck">
                  Recommend for Model Household certification
                </label>
              </div>
            </div>
          </div>

          <!-- Visit Stats Summary -->
          <div class="alert alert-light">
            <div class="row text-center">
              <div class="col-4">
                <h5 class="mb-0">{{ services.length }}</h5>
                <small class="text-muted">Services</small>
              </div>
              <div class="col-4">
                <h5 class="mb-0">{{ referrals.length }}</h5>
                <small class="text-muted">Referrals</small>
              </div>
              <div class="col-4">
                <h5 class="mb-0">{{ calculateDuration() }}</h5>
                <small class="text-muted">Minutes</small>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>
          <button type="button" class="btn btn-primary" @click="completeVisit"
                  :disabled="submitting || (completionForm.followUpRequired && !completionForm.followUpDate)">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Completing...
            </span>
            <span v-else>
              <i class="bx bx-check-circle"></i> Complete Visit
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "CompleteVisitModal",
  props: {
    visit: {
      type: Object,
      required: true
    },
    services: {
      type: Array,
      default: () => []
    },
    notes: {
      type: String,
      default: ''
    }
  },

  computed: {
    visitId() {
      // Handle different possible structures
      return this.visit?.visit?.visit_id || this.visit?.visit_id || null;
    },

    members() {
      // Get members from visit data
      return this.visit?.members || [];
    }
  },

  data() {
    return {
      submitting: false,

      // Completion form
      completionForm: {
        notes: '',
        followUpRequired: false,
        followUpDate: '',
        followUpPriority: 'medium',
        followUpReason: '',
        recommendModelStatus: false
      },

      // Findings
      findings: {
        healthIssues: [],
        environmentalConcerns: []
      },

      // Health issues options
      healthIssues: [
        { value: 'malnutrition', label: 'Malnutrition' },
        { value: 'respiratory', label: 'Respiratory Issues' },
        { value: 'diarrhea', label: 'Diarrhea' },
        { value: 'fever', label: 'Fever/Malaria' },
        { value: 'skin_conditions', label: 'Skin Conditions' },
        { value: 'pregnancy_complications', label: 'Pregnancy Complications' },
        { value: 'chronic_disease', label: 'Chronic Disease' },
        { value: 'mental_health', label: 'Mental Health' }
      ],

      // Environmental concerns options
      environmentalConcerns: [
        { value: 'water_unsafe', label: 'Unsafe Water' },
        { value: 'no_latrine', label: 'No Latrine' },
        { value: 'poor_hygiene', label: 'Poor Hygiene' },
        { value: 'waste_disposal', label: 'Waste Issues' },
        { value: 'vector_breeding', label: 'Vector Breeding' },
        { value: 'indoor_pollution', label: 'Indoor Pollution' }
      ],

      // Referrals
      referrals: [],
      showAddReferral: false,
      quickReferral: {
        memberId: '',
        referralTo: '',
        reason: ''
      },

      // Model household criteria
      metCriteria: [],
      canEvaluateModelHousehold: false,

      tomorrow: new Date(Date.now() + 86400000).toISOString().split('T')[0]
    };
  },

  mounted() {
    // Pre-populate notes if passed
    if (this.notes) {
      this.completionForm.notes = this.notes;
    }

    this.evaluateModelHouseholdCriteria();

    // Debug log to check structure
    console.log('Visit data structure:', this.visit);
    console.log('Computed visitId:', this.visitId);
  },

  methods: {
    calculateDuration() {
      const visitDate = this.visit?.visit?.visit_date || this.visit?.visit_date;
      if (!visitDate) return 0;

      const start = new Date(visitDate);
      const now = new Date();
      const diffInMinutes = Math.round((now - start) / 60000);

      return Math.max(diffInMinutes, 1);
    },

    evaluateModelHouseholdCriteria() {
      // This would normally check against actual criteria from the visit
      const criteria = [];

      // Check services provided during visit
      const hasEnvironmentalAssessment = this.services.some(s =>
          s.service_type === 'environmental' || s.service_category === 'environmental'
      );

      const hasHealthEducation = this.services.some(s =>
          s.service_type === 'health_education'
      );

      if (hasEnvironmentalAssessment) {
        criteria.push('Environmental assessment completed');
      }

      if (hasHealthEducation) {
        criteria.push('Health education provided');
      }

      // Check if all vaccinations are up to date
      const hasVaccinationService = this.services.some(s =>
          s.service_type === 'immunization'
      );

      if (hasVaccinationService) {
        criteria.push('Vaccinations up to date');
      }

      this.metCriteria = criteria;
      this.canEvaluateModelHousehold = criteria.length >= 2;
    },

    addQuickReferral() {
      if (!this.quickReferral.memberId || !this.quickReferral.referralTo) {
        return;
      }

      const member = this.members.find(m =>
          m.member_id === parseInt(this.quickReferral.memberId)
      );

      this.referrals.push({
        memberId: this.quickReferral.memberId,
        memberName: member ? `${member.first_name} ${member.surname}` : 'Unknown',
        referralTo: this.quickReferral.referralTo,
        reason: this.quickReferral.reason
      });

      this.cancelQuickReferral();
    },

    cancelQuickReferral() {
      this.showAddReferral = false;
      this.quickReferral = {
        memberId: '',
        referralTo: '',
        reason: ''
      };
    },

    removeReferral(index) {
      this.referrals.splice(index, 1);
    },

    async completeVisit() {
      // Validate visit ID
      if (!this.visitId) {
        alert('Error: Visit ID is missing. Please refresh and try again.');
        console.error('Visit ID not found in:', this.visit);
        return;
      }

      if (this.completionForm.followUpRequired && !this.completionForm.followUpDate) {
        alert('Please select a follow-up date');
        return;
      }

      this.submitting = true;

      // Combine notes with findings
      let fullNotes = this.completionForm.notes || '';

      if (this.findings.healthIssues.length > 0) {
        fullNotes += '\n\nHealth Issues Identified: ' + this.findings.healthIssues.join(', ');
      }

      if (this.findings.environmentalConcerns.length > 0) {
        fullNotes += '\nEnvironmental Concerns: ' + this.findings.environmentalConcerns.join(', ');
      }

      if (this.completionForm.followUpRequired && this.completionForm.followUpReason) {
        fullNotes += '\n\nFollow-up Reason: ' + this.completionForm.followUpReason;
      }

      const data = {
        "function": "completeVisit",
        "visitId": this.visitId.toString(), // Ensure it's a string
        "notes": fullNotes.trim(),
        "followUpRequired": this.completionForm.followUpRequired,
        "followUpDate": this.completionForm.followUpDate || null,
        "followUpPriority": this.completionForm.followUpPriority
      };

      console.log('Sending complete visit request:', data);

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // Create referrals if any
          for (const referral of this.referrals) {
            await this.createReferral(referral);
          }

          // Update model household status if recommended
          if (this.completionForm.recommendModelStatus) {
            await this.recommendModelHousehold();
          }

          this.$emit('completed', {
            visitId: this.visitId,
            duration: this.calculateDuration()
          });
        } else {
          alert(res.data.message || 'Failed to complete visit');
        }
      } catch (e) {
        console.error("Failed to complete visit:", e);
        alert('Failed to complete visit. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    async createReferral(referral) {
      const data = {
        "function": "createReferral",
        "memberId": referral.memberId,
        "visitId": this.visitId.toString(),
        "referralType": "routine",
        "referralReason": referral.reason,
        "referralTo": referral.referralTo,
        "urgency": "routine"
      };

      try {
        await executeJson(data, authUrl);
      } catch (e) {
        console.error("Failed to create referral:", e);
      }
    },

    async recommendModelHousehold() {
      const data = {
        "function": "recommendModelHousehold",
        "householdId": this.visit?.household?.household_id || this.visit?.household_id,
        "visitId": this.visitId.toString(),
        "criteria": this.metCriteria.join(', ')
      };

      try {
        await executeJson(data, authUrl);
      } catch (e) {
        console.error("Failed to recommend model household:", e);
      }
    },

    formatServiceType(serviceType) {
      if (!serviceType) return '';

      // Convert snake_case to Title Case
      return serviceType
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    },

    formatServiceDetails(detailsString) {
      if (!detailsString) return 'No details';

      try {
        const details = JSON.parse(detailsString);
        const parts = [];

        // Service name
        if (details.serviceName) {
          parts.push(details.serviceName);
        }

        // Specific details based on service type
        if (details.topic) {
          parts.push(`Topic: ${this.formatTopic(details.topic)}`);
        }

        if (details.wound_type) {
          parts.push(`Wound: ${this.formatTopic(details.wound_type)}`);
        }

        if (details.treatment) {
          parts.push(`Treatment: ${this.formatTopic(details.treatment)}`);
        }

        // Notes (if any and not empty)
        if (details.notes && details.notes.trim()) {
          parts.push(`Notes: ${details.notes}`);
        }

        // Referral needed
        if (details.referralNeeded) {
          parts.push('⚠️ Referral needed');
        }

        return parts.join(' | ');
      } catch (e) {
        // If parsing fails, return the original string
        return detailsString;
      }
    },

    formatTopic(topic) {
      if (!topic) return '';

      // Convert snake_case to Title Case
      return topic
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    },

    getVisitTypeLabel(type) {
      const labels = {
        'routine': 'Routine Health Promotion',
        'follow_up': 'Follow-up Visit',
        'emergency': 'Emergency/Urgent',
        'campaign': 'Campaign Activity',
        'assessment': 'Environmental Assessment',
        'pnc': 'Postnatal Care',
        'anc': 'Antenatal Care'
      };
      return labels[type] || type;
    },

    getServiceBadgeClass(category) {
      const classes = {
        'preventive': 'bg-primary',
        'clinical': 'bg-danger',
        'education': 'bg-info',
        'maternal': 'bg-warning',
        'child': 'bg-success',
        'referral': 'bg-danger',
        'other': 'bg-secondary'
      };

      return classes[category] || 'bg-secondary';
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.form-check-input {
  cursor: pointer;
}

.list-group-item {
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.card.border-primary {
  border-width: 2px;
}

.alert ul {
  margin-bottom: 0;
  padding-left: 20px;
}

.alert-light {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}

.form-label {
  font-weight: 600;
  color: #495057;
}

.text-capitalize {
  text-transform: capitalize;
}

/* Checkbox styling */
.form-check {
  padding-left: 1.5rem;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

/* Modal animations */
.modal {
  animation: fadeIn 0.3s ease-out;
}

.modal-dialog {
  animation: slideIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>